import React, { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  Box,
  Paper,
  Typography,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import axiosInstant from '../../../../api';
import config from '../../../../config';
import { adminAffinityLinks, adminAffinityLinkDelete } from '../../../../api/endpoints';
import { Button } from '../../../../components/Button/Button';
import { DotLottleLoader } from '../../../../components/Loader';
import { useAffinityGraph, useAffinityCategories, AffinityMode } from './useAffinityGraph';
import { AffinityGraph } from './AffinityGraph';
import { SubProfessionPicker, SubProfOption } from './SubProfessionPicker';
import { CategoryPicker, FocusOption } from './CategoryPicker';
import { ProfessionPicker } from './ProfessionPicker';
import { ProductPicker } from './ProductPicker';
import { CategoryLinkEditor } from './CategoryLinkEditor';
import { SubProfessionLinkEditor } from './SubProfessionLinkEditor';
import { AffinityGraphNode, AffinityGraphLink, AffinityCategory, Tier } from './affinity.types';
import { TIER_COLORS, PENDING_COLORS } from './affinity.constants';

interface Props {
  onAfterSave: () => void;
}

interface PendingNeighbor {
  id: number;
  name: string;
  type: 'category' | 'sub_profession';
  tier: Tier;
}

const Legend: React.FC = () => (
  <Box className="flex flex-wrap items-center gap-3">
    {[
      { c: TIER_COLORS.product, t: 'Product' },
      { c: TIER_COLORS.profession, t: 'Profession' },
      { c: TIER_COLORS.sub_profession, t: 'Sub-profession' },
      { c: TIER_COLORS.L1, t: 'L1 area' },
      { c: TIER_COLORS.L2, t: 'L2 family' },
      { c: TIER_COLORS.L3, t: 'L3 leaf' },
      { c: PENDING_COLORS.added, t: 'Pending add' },
      { c: PENDING_COLORS.removed, t: 'Pending remove' },
    ].map(({ c, t }) => (
      <Box key={t} className="flex items-center gap-1.5">
        <Box sx={{ width: 11, height: 11, borderRadius: '50%', bgcolor: c }} />
        <Typography sx={{ fontSize: 12, color: 'var(--text-2)' }}>{t}</Typography>
      </Box>
    ))}
  </Box>
);

// The id of the entity on the OTHER end of a link from the focus node.
const neighborIdOf = (node: AffinityGraphNode): number | undefined =>
  node.type === 'category' ? node.categoryId : node.subProfessionId;

const AffinityNetwork: React.FC<Props> = ({ onAfterSave }) => {
  // mode + focus are ONE atomic state: react-force-graph's onNodeClick fires
  // outside React's event batching (React 17), so two separate setState calls
  // would render an intermediate frame where the mode changed but the focus
  // hadn't — firing a fetch with the wrong id (e.g. category-graph/<productId>).
  const [view, setView] = useState<{ mode: AffinityMode; focus: FocusOption | null }>({
    mode: 'sub_profession',
    focus: null,
  });
  const { mode, focus } = view;
  // Staged edits relative to the loaded link set, keyed by the NEIGHBOR entity id.
  const [pendingAdds, setPendingAdds] = useState<Map<number, PendingNeighbor>>(new Map());
  const [pendingRemoves, setPendingRemoves] = useState<Set<number>>(new Set());
  const [saving, setSaving] = useState(false);

  const { data, loading, error, refetch } = useAffinityGraph(mode, focus?.id ?? null);
  const { categories } = useAffinityCategories();

  const editable = mode === 'sub_profession' || mode === 'category';
  const neighborType: 'category' | 'sub_profession' =
    mode === 'category' ? 'sub_profession' : 'category';
  const dirty = pendingAdds.size > 0 || pendingRemoves.size > 0;

  const clearPending = () => {
    setPendingAdds(new Map());
    setPendingRemoves(new Set());
  };

  const confirmDiscard = () => !dirty || window.confirm('Discard unsaved affinity changes?');

  const switchMode = (next: AffinityMode) => {
    if (!next || next === mode) return;
    if (!confirmDiscard()) return;
    clearPending();
    setView({ mode: next, focus: null });
  };

  const pickFocus = (next: FocusOption | null) => {
    if (!confirmDiscard()) return;
    clearPending();
    setView((v) => ({ ...v, focus: next }));
  };

  // Existing (server-side) neighbor ids for the loaded focus entity.
  const existingIds = useMemo(() => {
    const ids = new Set<number>();
    (data?.nodes || []).forEach((n) => {
      if (n.id === data?.root.id) return;
      const id = neighborIdOf(n);
      if (id != null) ids.add(id);
    });
    return ids;
  }, [data]);

  const linkedNeighborIds = useMemo(() => {
    const ids = new Set<number>();
    existingIds.forEach((id) => {
      if (!pendingRemoves.has(id)) ids.add(id);
    });
    pendingAdds.forEach((_, id) => ids.add(id));
    return ids;
  }, [existingIds, pendingRemoves, pendingAdds]);

  // Build the graph the user sees.
  const { nodes, links } = useMemo(() => {
    const outNodes: AffinityGraphNode[] = [];
    const outLinks: AffinityGraphLink[] = [];
    if (!data) return { nodes: outNodes, links: outLinks };

    // Read-only profession overview: render the server graph verbatim.
    if (!editable) {
      return {
        nodes: (data.nodes || []).map((n) => ({ ...n })),
        links: (data.links || []).map((l) => ({ ...l })),
      };
    }

    const rootId = data.root.id;
    (data.nodes || []).forEach((n) => {
      if (n.id === rootId) {
        outNodes.push({ ...n });
        return;
      }
      const nid = neighborIdOf(n);
      const removed = nid != null && pendingRemoves.has(nid);
      outNodes.push({ ...n, pendingState: removed ? 'removed' : undefined });
      outLinks.push({ source: rootId, target: n.id, pendingState: removed ? 'removed' : undefined });
    });

    pendingAdds.forEach((nb, id) => {
      const nodeId = `${nb.type === 'category' ? 'cat' : 'sp'}-${id}`;
      outNodes.push({
        id: nodeId,
        type: nb.type,
        name: nb.name,
        tier: nb.tier,
        categoryId: nb.type === 'category' ? id : undefined,
        subProfessionId: nb.type === 'sub_profession' ? id : undefined,
        pendingState: 'added',
      });
      outLinks.push({ source: rootId, target: nodeId, pendingState: 'added' });
    });

    return { nodes: outNodes, links: outLinks };
  }, [data, editable, pendingRemoves, pendingAdds]);

  const handleNodeClick = (node: AffinityGraphNode) => {
    // In the read-only overview, a node click navigates into its focused editor.
    if (!editable) {
      if (node.type === 'sub_profession' && node.subProfessionId != null) {
        if (!confirmDiscard()) return;
        clearPending();
        setView({ mode: 'sub_profession', focus: { id: node.subProfessionId, name: node.name } });
      } else if (node.type === 'category' && node.categoryId != null) {
        if (!confirmDiscard()) return;
        clearPending();
        setView({ mode: 'category', focus: { id: node.categoryId, name: node.name } });
      }
      return;
    }

    // Editable modes: toggle the neighbor link.
    if (data && node.id === data.root.id) return;
    const id = neighborIdOf(node);
    if (id == null) return;

    if (pendingAdds.has(id)) {
      setPendingAdds((prev) => {
        const next = new Map(prev);
        next.delete(id);
        return next;
      });
      return;
    }
    if (existingIds.has(id)) {
      setPendingRemoves((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    }
  };

  const addNeighbor = (nb: PendingNeighbor) => {
    if (existingIds.has(nb.id)) {
      // Re-adding an existing-but-removed link cancels the removal.
      setPendingRemoves((prev) => {
        const next = new Set(prev);
        next.delete(nb.id);
        return next;
      });
      return;
    }
    setPendingAdds((prev) => new Map(prev).set(nb.id, nb));
  };

  const handleAddCategory = (cat: AffinityCategory) =>
    addNeighbor({ id: cat.id, name: cat.name, type: 'category', tier: cat.tier });
  const handleAddSubProfession = (sub: SubProfOption) =>
    addNeighbor({ id: sub.id, name: sub.name, type: 'sub_profession', tier: null });

  // Resolve a staged neighbor id into the (sub_profession_id, product_category_id)
  // pair the junction expects, based on which side is the focus.
  const linkPair = (neighborId: number) =>
    mode === 'category'
      ? { sub_profession_id: neighborId, product_category_id: focus!.id }
      : { sub_profession_id: focus!.id, product_category_id: neighborId };

  const save = async () => {
    // Staged ids are derived from the loaded graph — never write without one
    // (e.g. after a fetch error replaced it), the pairs would be meaningless.
    if (!focus || !data) return;
    setSaving(true);
    try {
      const requests: Promise<unknown>[] = [];
      pendingAdds.forEach((_, id) => {
        requests.push(axiosInstant.post(`${config.API_URL}${adminAffinityLinks}`, linkPair(id)));
      });
      pendingRemoves.forEach((id) => {
        const { sub_profession_id: sp, product_category_id: cat } = linkPair(id);
        requests.push(axiosInstant.delete(`${config.API_URL}${adminAffinityLinkDelete(sp, cat)}`));
      });
      await Promise.all(requests);
      toast.success('Affinity links saved — run a recompute to apply them to scores');
      clearPending();
      await refetch();
      onAfterSave();
    } catch (err) {
      toast.error('Failed to save affinity links');
    } finally {
      setSaving(false);
    }
  };

  const emptyMessages: Record<AffinityMode, string> = {
    category: 'Pick a category to view the sub-professions linked to it.',
    profession: 'Pick a profession to view its full sub-profession → category net.',
    product: 'Pick a product to view its categories → matching sub-professions net.',
    sub_profession: 'Pick a sub-profession to view its category affinities.',
  };
  const emptyMsg = emptyMessages[mode];

  return (
    <Paper sx={{ p: 3 }}>
      <Box className="flex flex-wrap items-center justify-between gap-3 pb-1">
        <Typography fontWeight={600}>Affinity network</Typography>
        {editable && (
          <Box className="flex items-center gap-3">
            <Typography color="var(--text-2)" sx={{ fontSize: 13 }}>
              {dirty ? `${pendingAdds.size} add · ${pendingRemoves.size} remove pending` : 'No pending changes'}
            </Typography>
            <Button
              onClick={() => clearPending()}
              disabled={!dirty || saving}
              className="!px-4 !bg-surface !text-accent-text !border !border-accent"
            >
              <Typography>Reset</Typography>
            </Button>
            <Button onClick={save} disabled={!dirty || saving} className="!px-5">
              <Typography>{saving ? 'Saving…' : 'Save links'}</Typography>
            </Button>
          </Box>
        )}
      </Box>
      <Typography color="var(--text-2)" sx={{ fontSize: 14, pb: 2 }}>
        Explore the sub-profession ⇄ product-category affinity from any side. Edit
        from a sub-profession or a category; the profession view is a read-only
        overview (click any node to drill in). Nothing is written until you Save.
      </Typography>

      <ToggleButtonGroup
        size="small"
        exclusive
        value={mode}
        onChange={(_, v) => v && switchMode(v)}
        sx={{ mb: 2 }}
      >
        <ToggleButton value="sub_profession">By sub-profession</ToggleButton>
        <ToggleButton value="category">By category</ToggleButton>
        <ToggleButton value="profession">By profession</ToggleButton>
        <ToggleButton value="product">By product</ToggleButton>
      </ToggleButtonGroup>

      <Divider sx={{ mb: 2 }} />

      <Box className="flex flex-col gap-4 md:flex-row">
        {/* Left rail: picker + (editable) add editor */}
        <Box sx={{ width: '100%', maxWidth: 320 }} className="flex flex-col gap-4 flex-shrink-0">
          {mode === 'sub_profession' && (
            <SubProfessionPicker
              value={focus ? { id: focus.id, name: focus.name, professionName: '' } : null}
              onChange={(v) => pickFocus(v ? { id: v.id, name: v.name } : null)}
            />
          )}
          {mode === 'category' && (
            <CategoryPicker categories={categories} value={focus} onChange={pickFocus} />
          )}
          {mode === 'profession' && <ProfessionPicker value={focus} onChange={pickFocus} />}
          {mode === 'product' && <ProductPicker value={focus} onChange={pickFocus} />}

          {editable && focus && neighborType === 'category' && (
            <CategoryLinkEditor
              categories={categories}
              linkedCategoryIds={linkedNeighborIds}
              onAdd={handleAddCategory}
              disabled={saving}
            />
          )}
          {editable && focus && neighborType === 'sub_profession' && (
            <SubProfessionLinkEditor
              linkedSubProfessionIds={linkedNeighborIds}
              onAdd={handleAddSubProfession}
              disabled={saving}
            />
          )}
        </Box>

        {/* Graph */}
        <Box className="flex flex-col gap-2" sx={{ flex: 1, minWidth: 0 }}>
          <Legend />
          {!focus && (
            <Box
              className="flex items-center justify-center"
              sx={{ height: 520, border: '1px dashed var(--border)', borderRadius: 1.5, color: 'var(--muted)' }}
            >
              <Typography>{emptyMsg}</Typography>
            </Box>
          )}
          {focus && loading && (
            <Box className="flex items-center justify-center" sx={{ height: 520 }}>
              <DotLottleLoader />
            </Box>
          )}
          {focus && !loading && error && (
            <Box
              className="flex flex-col items-center justify-center gap-3"
              sx={{ height: 520, border: '1px dashed var(--border)', borderRadius: 1.5, color: 'var(--muted)' }}
            >
              <Typography>Failed to load the affinity graph.</Typography>
              <Button onClick={() => refetch()} className="!px-4">
                <Typography>Retry</Typography>
              </Button>
            </Box>
          )}
          {focus && !loading && !error && data && (
            <>
              {editable && nodes.filter((n) => n.id !== data.root.id).length === 0 && !dirty && (
                <Typography color="var(--text-2)" sx={{ fontSize: 13 }}>
                  {neighborType === 'category'
                    ? 'No categories linked yet — add the first one on the left.'
                    : 'No sub-professions linked yet — add the first one on the left.'}
                </Typography>
              )}
              <AffinityGraph
                nodes={nodes}
                links={links}
                rootId={data.root.id}
                onNodeClick={handleNodeClick}
              />
            </>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

export default AffinityNetwork;
